/**
 * Roles and capabilities — ADR-001 §6.2.
 *
 * HARD RULE: no person is hard-wired. Everything a screen shows or a button allows is derived from the
 * signed-in user's `role` (data in `users.role`) and their SPOC assignments (rows in `client_spocs`).
 * The SPA must render from `GET /me` → `capabilities` and `menu`; it must not branch on role literals in
 * its data-fetching path, and never on a person.
 *
 * The role → capability table itself lives in `@amplispot/domain` (`POLICY`), so that the API's `/me`,
 * its scope checks and the test matrix all read the same object.
 */
import { z } from 'zod';
export const ROLES = ['founder', 'ops_ad', 'ops_member', 'sales', 'marketing', 'hr'];
export const Role = z.enum(ROLES).meta({
    id: 'Role',
    description: 'founder | ops_ad (Ops associate director) | ops_member | sales | marketing | hr. Any number of users per role.',
});
/** manager = {founder, ops_ad} — sees all clients, revenue, Team, Money, Settings. */
export const MANAGER_ROLES = ['founder', 'ops_ad'];
/** requester = {sales, marketing} — raises and follows own requests only. */
export const REQUESTER_ROLES = ['sales', 'marketing'];
/** Roles that may hold a client as its SPOC (the prototype's Ops list includes the associate director). */
export const SPOC_ELIGIBLE_ROLES = ['ops_ad', 'ops_member'];
export const isManagerRole = (r) => MANAGER_ROLES.includes(r);
export const isRequesterRole = (r) => REQUESTER_ROLES.includes(r);
/** Display labels, as written in the product guide §1–2. */
export const ROLE_LABEL = {
    founder: 'Founder',
    ops_ad: 'Ops associate director',
    ops_member: 'Ops member',
    sales: 'Sales',
    marketing: 'Marketing',
    hr: 'HR',
};
/**
 * Every capability the server can grant. `GET /me` returns the subset the caller holds.
 * Names: `<resource>.<action>[.<scope>]`. Adding one is additive (non-breaking).
 */
export const CAPABILITIES = [
    // Today
    'today.company', // founder/ops_ad exception feed
    'today.own', // ops_member single Today page with "Needs you"
    // Work
    'tasks.read.all',
    'tasks.read.own',
    'task.reassign',
    'changeRequest.raise',
    'changeRequest.view',
    'changeRequest.decide',
    'proof.check',
    /** Operator decision 12: when the associate director closed a task, the founder checks the proof. */
    'proof.check.ad_closures',
    // Clients
    'clients.read.all',
    'clients.read.own',
    'clients.create',
    'clients.edit.all', // includes priority, strategic, priority_note
    'clients.edit.own', // own clients, non-revenue, non-priority fields
    'client.changeSpoc',
    'client.emailDomains.write',
    'revenue.read',
    // Usage
    'usage.read.all',
    'usage.read.own',
    'usage.upload',
    // Team / timesheets
    'team.read',
    'timesheet.read.all',
    'timesheet.read.own',
    'timesheet.file',
    /**
     * The timesheet gate applies: a missed previous working day blocks planning today, raises the manager "has not
     * filed" exception and can be nudged. Ops members; NOT the Ops associate director, who files but is never
     * gated (operator ruling 2026-10-16).
     */
    'timesheet.gated',
    'timesheet.analysis.read',
    'timesheet.feedback.write',
    // Checklists
    'checklist.write.any',
    'checklist.write.own',
    // Requests
    'requests.raise',
    'requests.read.own',
    'requests.triage',
    // Money
    'money.read',
    // Settings, people, audit
    'settings.write',
    /** System-level settings (AI provider and similar). Founder by default (operator decision 2026-10-16). */
    'settings.system',
    'users.read',
    'users.write',
    'audit.read',
    /** May act as / view as a lower-ranked user (operator decisions 14, 17, 18; ADR-006). Every write records both people. */
    'actAs.member',
    // Holiday calendar — HR defines and publishes the yearly calendar used by all working-day maths
    'holidays.manage',
    // Everything else
    'capture.use',
    'ideas.submit',
    'ideas.decide',
    'toneReason.read',
];
export const Capability = z.enum(CAPABILITIES).meta({ id: 'Capability' });
/**
 * Capabilities that may also be granted to one person, on top of their role (operator decisions 11–12,
 * 2026-10-16). `holidays.manage` is ADR-005's `hr.admin`: the `hr` role carries it, and Settings → People can
 * grant it to anyone — the founder holds it at go-live. A grant adds only the capability itself: no client,
 * task, timesheet, usage or revenue sight (ADR-005 §2). Managed with PUT/DELETE /users/{id}/capabilities/{capability}.
 */
export const GRANTABLE_CAPABILITIES = ['holidays.manage'];
export const GrantableCapability = z.enum(GRANTABLE_CAPABILITIES).meta({ id: 'GrantableCapability' });
//# sourceMappingURL=roles.js.map